import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { http, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const empty = {
    slug: "", title: "", year: new Date().getFullYear(), category: "Commercial",
    client: "", cover: "", video_url: "", description: "", credits: "",
    featured: false, order: 0, images: [],
};

export default function AdminDashboard() {
    const { user, ready, logout } = useAuth();
    const nav = useNavigate();
    const [projects, setProjects] = useState([]);
    const [messages, setMessages] = useState([]);
    const [editing, setEditing] = useState(null); // slug being edited or "__new__"
    const [form, setForm] = useState(empty);

    useEffect(() => {
        if (ready && !user) nav("/admin/login", { replace: true });
    }, [ready, user, nav]);

    const refresh = async () => {
        const [p, m] = await Promise.all([
            http.get("/projects"),
            http.get("/admin/messages").catch(() => ({ data: [] })),
        ]);
        setProjects(p.data);
        setMessages(m.data);
    };

    useEffect(() => {
        if (user) refresh();
    }, [user]);

    if (!user) return <main className="min-h-screen bg-black" />;

    const startNew = () => {
        setForm(empty);
        setEditing("__new__");
    };

    const startEdit = (p) => {
        setForm({ ...empty, ...p, images: p.images || [] });
        setEditing(p.slug);
    };

    const save = async () => {
        try {
            if (editing === "__new__") {
                await http.post("/admin/projects", form);
                toast.success("Proyecto creado");
            } else {
                await http.put(`/admin/projects/${editing}`, form);
                toast.success("Proyecto actualizado");
            }
            setEditing(null);
            await refresh();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        }
    };

    const remove = async (slug) => {
        if (!confirm(`¿Eliminar "${slug}"?`)) return;
        await http.delete(`/admin/projects/${slug}`);
        toast.success("Eliminado");
        await refresh();
    };

    return (
        <main data-testid="admin-dashboard" className="min-h-screen px-6 md:px-12 pt-28 pb-20">
            <header className="flex items-center justify-between mb-12">
                <div>
                    <p className="text-[10px] tracking-[0.32em] uppercase text-white/40">Studio CMS</p>
                    <h1 className="font-display tracking-tightest text-4xl md:text-5xl">Dashboard</h1>
                </div>
                <div className="flex items-center gap-6 text-sm">
                    <span className="text-white/60 hidden md:inline">{user.email}</span>
                    <button data-testid="admin-logout" onClick={async () => { await logout(); nav("/admin/login"); }} className="midi-link text-white">
                        Salir
                    </button>
                </div>
            </header>

            {editing ? (
                <Editor form={form} setForm={setForm} onCancel={() => setEditing(null)} onSave={save} />
            ) : (
                <>
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="font-display tracking-tightest text-2xl">Proyectos ({projects.length})</h2>
                        <button data-testid="admin-new-project" onClick={startNew} className="midi-link text-white text-sm tracking-[0.32em] uppercase">
                            + Nuevo proyecto
                        </button>
                    </div>
                    <ul className="border-t border-white/10">
                        {projects.map((p) => (
                            <li key={p.slug} className="border-b border-white/10 py-5 flex items-center justify-between gap-4">
                                <div className="flex items-center gap-5 min-w-0">
                                    <img src={p.cover} alt="" className="w-20 h-12 object-cover" />
                                    <div className="min-w-0">
                                        <p className="font-display tracking-tightest text-xl truncate">{p.title}</p>
                                        <p className="text-xs text-white/40">
                                            {p.category} — {p.year} — {p.slug}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-5 text-sm shrink-0">
                                    <button onClick={() => startEdit(p)} className="midi-link text-white">Editar</button>
                                    <button onClick={() => remove(p.slug)} className="midi-link text-red-400">Eliminar</button>
                                </div>
                            </li>
                        ))}
                    </ul>

                    <h2 className="font-display tracking-tightest text-2xl mt-20 mb-6">Mensajes ({messages.length})</h2>
                    <ul className="border-t border-white/10">
                        {messages.map((m) => (
                            <li key={m.id} className="border-b border-white/10 py-5">
                                <div className="flex justify-between text-xs text-white/40 mb-2">
                                    <span>{m.name} · {m.email}{m.company ? ` · ${m.company}` : ""}</span>
                                    <span>{new Date(m.created_at).toLocaleString("es-ES")}</span>
                                </div>
                                <p className="text-white/85 text-sm leading-relaxed whitespace-pre-wrap">{m.message}</p>
                            </li>
                        ))}
                        {messages.length === 0 && <li className="py-5 text-sm text-white/40">Aún no hay mensajes.</li>}
                    </ul>
                </>
            )}
        </main>
    );
}

function Editor({ form, setForm, onCancel, onSave }) {
    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
    return (
        <div data-testid="admin-editor" className="space-y-5 max-w-3xl">
            <div className="flex items-center justify-between mb-2">
                <h2 className="font-display tracking-tightest text-3xl">Proyecto</h2>
                <button onClick={onCancel} className="midi-link text-white/70 text-sm">Cancelar</button>
            </div>
            {[
                ["slug", "Slug (url)"],
                ["title", "Título"],
                ["year", "Año"],
                ["category", "Categoría"],
                ["client", "Cliente"],
                ["cover", "URL de portada"],
                ["video_url", "URL de vídeo (opcional)"],
                ["credits", "Créditos"],
            ].map(([k, label]) => (
                <div key={k}>
                    <label className="text-[10px] tracking-[0.32em] uppercase text-white/40 block mb-2">{label}</label>
                    <input
                        data-testid={`admin-field-${k}`}
                        value={form[k] ?? ""}
                        onChange={set(k)}
                        className="w-full bg-transparent border-b border-white/20 focus:border-[color:var(--midi-accent-strong)] outline-none py-2"
                    />
                </div>
            ))}
            <div>
                <label className="text-[10px] tracking-[0.32em] uppercase text-white/40 block mb-2">Descripción</label>
                <textarea
                    data-testid="admin-field-description"
                    rows={4}
                    value={form.description ?? ""}
                    onChange={set("description")}
                    className="w-full bg-transparent border-b border-white/20 focus:border-[color:var(--midi-accent-strong)] outline-none py-2"
                />
            </div>
            <div>
                <label className="text-[10px] tracking-[0.32em] uppercase text-white/40 block mb-2">
                    Imágenes (una URL por línea)
                </label>
                <textarea
                    rows={4}
                    value={(form.images || []).join("\n")}
                    onChange={(e) => setForm({ ...form, images: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })}
                    className="w-full bg-transparent border-b border-white/20 focus:border-[color:var(--midi-accent-strong)] outline-none py-2"
                />
            </div>
            <button
                data-testid="admin-save"
                onClick={onSave}
                className="font-display tracking-tightest text-2xl border-b border-white/30 hover:border-[color:var(--midi-accent-strong)] pb-3 transition-colors duration-500"
            >
                Guardar →
            </button>
        </div>
    );
}
